import { Button, Card, Flex, Tabs } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInvestigationsServiceGetListSpecimensEndpoint,
  useTreatmentServiceGetTreatmentPlanListEndpoint
} from 'src/api/queries'
import {
  Gender,
  ListSortDirection,
  MedicalAssistanceType,
  Permissions,
  SearchSpecimenSortBy
} from 'src/api/requests'
import { LoadingScreen, PageBreadcrumbs } from 'src/components'
import { TestStatusTag } from 'src/features/tests/components'
import { getFullName } from 'src/features/users/utils'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot, formatUtcDateTime } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import {
  CaseStatusTag,
  PersonSummaryCard,
  PersonSummaryDetailList,
  RegisterPatientModal,
  TransferModal
} from '../components'
import { TreatmentStatusTag } from '../components/PersonTreatmentsSection/components/TreatmentStatusTag'
import { usePersonBreadcrumb } from '../hooks'

export const PersonsDetailsPage = () => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()
  const { hasPermission } = useUser()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)

  const { breadcrumbItems, fullName, isLoadingPerson, person } =
    usePersonBreadcrumb(id)

  const isPatient = !!person?.simeCode || !!person?.tarvCode

  const onToggle = () => setIsModalOpen((prev) => !prev)
  const onTransferToggle = () => setIsTransferModalOpen((prev) => !prev)

  const personDetailsMap = useMemo(() => {
    if (!person) return []

    const baseDetailsMap = [
      {
        key: 'details',
        path: (id: string) => paths.persons.details(id)
      },
      ...(!person.isAnonymous
        ? [
            {
              key: 'risk-groups',
              path: () => paths.persons.riskGroups.root(),
              permission: Permissions.PERSONS_MENU_ACCESS_PATIENTS
            },
            {
              key: 'consultations',
              path: () => paths.persons.consultations.root(),
              permission: Permissions.TREATMENT_CONSULTATION_VIEW_INSTITUTION
            }
          ]
        : []),

      {
        key: 'screening',
        path: () => paths.persons.screening.root()
      }
    ]

    return baseDetailsMap
  }, [person])

  const segmentedOptions = useMemo(
    () =>
      personDetailsMap.map(({ key }) => ({
        key,
        label: t(`persons.person_${key}`)
      })),
    [t, personDetailsMap]
  )

  const activeKey = useMemo(() => {
    const lastSegment = location.pathname.split('/').filter(Boolean).pop()

    // The investigation-results page is a drill-down opened from a table, not
    // its own tab. Keep the originating tab (investigations/screening) active.
    if (lastSegment === 'investigation-results') {
      return searchParams.get('from') ?? 'screening'
    }

    return (
      personDetailsMap.find(({ key }) => key === lastSegment)?.key ?? 'details'
    )
  }, [location.pathname, personDetailsMap, searchParams])

  const onChange = (val: string | number) => {
    const selectedTab = personDetailsMap.find(({ key }) => key === val)
    if (selectedTab) {
      navigate(selectedTab.path(String(id)))
    }
  }

  const { data: institution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      {
        id: String(person?.institution?.id)
      },
      [String(person?.institution?.id)],
      { enabled: !!person?.institution?.id }
    )

  const isPenitentiary =
    institution?.structure?.medicalAssistanceTypes?.includes(
      MedicalAssistanceType.PENITENTIARY
    )

  const canTransferPatient =
    hasPermission(Permissions.PERSONS_TRANSFER_REQUEST_CREATE_INSTITUTION) ||
    isPenitentiary

  const personAddress = useMemo(() => {
    const address = person?.currentAddress
    if (!address) return '-'

    return (
      [
        address.cuatmLocality?.translation?.[currentLang.value],
        address.street,
        address.streetNumber,
        address.apartmentNumber
      ]
        .filter(Boolean)
        .join(', ') || '-'
    )
  }, [person, currentLang.value])

  const personalDetailsItems = useMemo(
    () => [
      {
        label: `${t('form.first_name')} ${t('form.last_name')}`,
        value: getFullName(person?.firstName, person?.lastName) || '-'
      },
      {
        label: t('common.testrap_code'),
        value: person?.testrapCode || '-'
      },
      { label: t('common.sime_code'), value: person?.simeCode || '-' },
      { label: t('common.tarv_code'), value: person?.tarvCode || '-' },
      { label: t('form.idnp'), value: person?.idnp || '-' },
      {
        label: t('form.gender'),
        value:
          person?.gender === Gender.MALE
            ? t('form.male')
            : person?.gender === Gender.FEMALE
              ? t('form.female')
              : '-'
      },
      {
        label: t('form.birth_date'),
        value: person?.birthDate
          ? dayjs(person.birthDate).format(dayMonthYearFormatWithDot)
          : '-'
      },
      { label: t('common.phone'), value: person?.phone || '-' },
      { label: t('form.address'), value: personAddress },
      {
        label: t('common.institution'),
        value: person?.institution?.name || '-'
      }
    ],
    [t, person, personAddress]
  )

  const { data: latestSpecimen } =
    useInvestigationsServiceGetListSpecimensEndpoint(
      {
        pageNumber: 1,
        pageSize: 1,
        personIds: [String(id)],
        sortBy: SearchSpecimenSortBy.CREATED_AT,
        sortDirection: ListSortDirection.DESCENDING
      },
      [String(id)],
      { enabled: !!id }
    )

  const { data: latestTreatment } =
    useTreatmentServiceGetTreatmentPlanListEndpoint(
      { pageNumber: 1, pageSize: 1, personId: String(id) },
      [String(id)],
      { enabled: !!id }
    )

  const specimen = latestSpecimen?.data?.[0]
  const treatment = latestTreatment?.data?.[0]

  const screeningItems = [
    {
      label: t('investigations.investigation_number'),
      value: specimen?.code || '-'
    },
    {
      label: t('consultations.specimen_type'),
      value: specimen?.type?.translation?.[currentLang.value] || '-'
    },
    {
      label: t('investigations.harvest_date'),
      value: formatUtcDateTime(specimen?.harvestDate)
    },
    {
      label: t('form.status'),
      value: specimen?.status ? <TestStatusTag status={specimen.status} /> : '-'
    },
    {
      label: t('cases.primary_request_reason'),
      value:
        specimen?.demand?.investigationReason?.translation?.[
          currentLang.value
        ] || '-'
    },
    {
      label: t('cases.secondary_request_reason'),
      value:
        specimen?.demand?.secondaryInvestigationReasons
          ?.map((reason) => reason?.translation?.[currentLang.value])
          .filter(Boolean)
          .join(', ') || '-'
    },
    ...(person?.caseStatus
      ? [
          {
            label: t('persons.case_status_label'),
            value: <CaseStatusTag status={person.caseStatus} />
          }
        ]
      : [])
  ]

  const treatmentItems = treatment
    ? [
        {
          label: t('persons.treatment_type_label'),
          value:
            (currentLang.value === 'ro'
              ? treatment.translation?.ro
              : treatment.translation?.ru) || '-'
        },
        {
          label: t('persons.start_date_label'),
          value: formatUtcDateTime(treatment.startDate)
        },
        {
          label: t('persons.end_date_label'),
          value: formatUtcDateTime(treatment.endDate)
        },
        {
          label: t('form.status'),
          value: <TreatmentStatusTag status={treatment.status} />
        }
      ]
    : []

  if (isLoadingPerson) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={fullName}
      items={breadcrumbItems}
      patientInformation={
        <Flex gap={8} wrap="wrap" style={{ width: '100%', minWidth: 0 }}>
          <PersonSummaryCard
            title={t('persons.personal_details_card')}
            variant="purple"
            statusText={
              <PersonSummaryDetailList
                variant="purple"
                items={personalDetailsItems}
              />
            }
          />

          <PersonSummaryCard
            title={t('persons.screening_investigation_card')}
            variant="green"
            statusText={
              <PersonSummaryDetailList variant="green" items={screeningItems} />
            }
          />

          {!!treatmentItems.length && (
            <PersonSummaryCard
              title={t('persons.treatment_card')}
              variant="yellow"
              statusText={
                <PersonSummaryDetailList
                  variant="yellow"
                  items={treatmentItems}
                />
              }
            />
          )}
        </Flex>
      }
      extra={
        <Flex gap={8}>
          {canTransferPatient && (
            <Button type="primary" onClick={onTransferToggle}>
              {t('persons.transfer')}
            </Button>
          )}

          {!isPatient &&
            hasPermission(
              Permissions.PERSONS_PERSONS_CREATE_ACCOUNT_INSTITUTION
            ) && (
              <Flex>
                <Button type="primary" onClick={onToggle}>
                  {t('persons.register_as_patient')}
                </Button>
              </Flex>
            )}
        </Flex>
      }
    >
      <Card>
        <Flex vertical gap={32}>
          <Tabs
            type="card"
            items={segmentedOptions}
            activeKey={activeKey}
            onChange={onChange}
          />
          <Outlet />
        </Flex>
      </Card>

      {isModalOpen && (
        <RegisterPatientModal personId={person?.id} onClose={onToggle} />
      )}

      {isTransferModalOpen && (
        <TransferModal data={person} onCancel={onTransferToggle} />
      )}
    </PageBreadcrumbs>
  )
}
